import styled from "@emotion/styled";
import { Instant, LocalDate, LocalDateTime } from "@js-joda/core";
import { LayoutGroup } from "framer-motion";
import React from "react";
import { Geo, PatientTaskBroadcastDetails, PatientTaskInstanceId } from "../../../messages";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { Visit } from "../../../system/AppState/AppVisitsState";
import { List } from "../../../utils/List";
import { comparable, reverseOrd } from "../../../utils/OrdTuple";
import { calcGeoDistance } from "../../DistanceLabel";
import RaffleBanner from "../../Raffle/components/RaffleBanner";
import { RaffleBannerData } from "../../Raffle/Raffle.types";
import useMainVisitSortingPreference, {
  MainVisitSortingPreference,
} from "../hooks/useMainVisitSortingPreference";
import VisitMapListedCards from "./VisitMapListedCards";

type Props = {
  caregiver: AppCaregiver;
  visits: List<Visit>;
  visitsOutsideAvailability: List<Visit>;
  tasks: List<PatientTaskBroadcastDetails>;
  raffle: RaffleBannerData | null;
  onClickRaffleBanner: () => void;
  onClickVisit: (visit: Visit) => void;
  onClickTask: (taskInstanceId: PatientTaskInstanceId) => void;
  onClickPreferenceSettings: () => void;
};

export function MainLayoutCardsView(props: Props) {
  const [sorting] = useMainVisitSortingPreference();

  const visits = React.useMemo(
    () => visitsSortingOrd(props.caregiver.addressGeoLocation, props.visits, sorting),
    [props.caregiver.addressGeoLocation, props.visits, sorting]
  );

  const tasks = React.useMemo(
    () => tasksSortingOrd(props.caregiver.addressGeoLocation, props.tasks, sorting),
    [props.caregiver.addressGeoLocation, props.tasks, sorting]
  );

  return (
    <Root>
      {props.raffle && (
        <RaffleBannerWrapper>
          <RaffleBanner
            prize={props.raffle.prize}
            tokens={props.raffle.tokens}
            daysLeft={props.raffle.daysLeft}
            progressPercentage={props.raffle.progressPercentage}
            onClick={props.onClickRaffleBanner}
          />
        </RaffleBannerWrapper>
      )}
      <LayoutGroup>
        <VisitMapListedCards
          caregiver={props.caregiver}
          visits={visits}
          visitsOutsideAvailability={props.visitsOutsideAvailability}
          tasks={tasks}
          onClickVisit={props.onClickVisit}
          onClickTask={props.onClickTask}
          onClickPreferenceSettings={props.onClickPreferenceSettings}
        />
      </LayoutGroup>
    </Root>
  );
}

function visitsSortingOrd(
  caregiverGeo: Geo,
  visits: List<Visit.Available | Visit.Onboarding>,
  sorting: MainVisitSortingPreference
): List<Visit.Available | Visit.Onboarding> {
  return visits.sortBy((visit) =>
    sortFn({
      caregiverGeo: caregiverGeo,
      sorting: sorting,
      createdAt: visit.publicVisitBroadcastDetails.createdAt,
      location: visit.publicVisitBroadcastDetails.location.location,
      price: visit.publicVisitBroadcastDetails.price,
      startTimeLocal: visit.publicVisitBroadcastDetails.startTimeLocal,
      priority: 0,
    })
  );
}

function tasksSortingOrd(
  caregiverGeo: Geo,
  tasks: List<PatientTaskBroadcastDetails>,
  sorting: MainVisitSortingPreference
): List<PatientTaskBroadcastDetails> {
  return tasks.sortBy((task) =>
    sortFn({
      caregiverGeo: caregiverGeo,
      sorting: sorting,
      createdAt: task.createdAt,
      location: task.patientAddressComponents?.location ?? null,
      price: null,
      startTimeLocal: task.startDate,
      priority: task.priority,
    })
  );
}

function sortFn(params: {
  caregiverGeo: Geo;
  createdAt: Instant;
  startTimeLocal: LocalDate | LocalDateTime;
  location: Geo | null;
  price: number | null;
  sorting: MainVisitSortingPreference;
  priority: number;
}) {
  const { caregiverGeo, createdAt, location, price, startTimeLocal, sorting, priority } = params;

  switch (sorting) {
    case "PostedDate": {
      return reverseOrd(createdAt);
    }
    case "StartDate": {
      return startTimeLocal;
    }
    case "Distance": {
      if (location === null) {
        return 0;
      }

      const distanceResult: number = calcGeoDistance(caregiverGeo, location, "K") * 1000;

      return distanceResult;
    }
    case "Priority": {
      return comparable(priority, startTimeLocal);
    }
    case "Price": {
      return reverseOrd(price !== null ? price : 0);
    }
  }
}

const Root = styled.div`
  background-color: var(--tw-slate-50);
  overflow: auto;
  flex-direction: column;
  flex: 1;
  height: 100%;
`;

const RaffleBannerWrapper = styled.div`
  padding: 16px 16px 0 16px;
`;
